import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";
import {
  CreditCard,
  Smartphone,
  Landmark,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { PortalState } from "@/store/portal-store";

interface Step9Props {
  state: PortalState;
  updateState: (partial: Partial<PortalState>) => void;
  onNext: () => void;
}

export function Step9Payment({ state, updateState, onNext }: Step9Props) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "card">("card");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [bankConnecting, setBankConnecting] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [bankError, setBankError] = useState(false);

  const premium = state.quote?.monthlyPremium ?? 0;
  const cardValid =
    cardNumber.replace(/\s/g, "").length === 16 &&
    expiry.length === 5 &&
    cvc.length >= 3;

  const formatCardNumber = (val: string) => {
    const nums = val.replace(/\D/g, "").slice(0, 16);
    return nums.replace(/(\d{4})(?=\d)/g, "$1 ");
  };

  const formatExpiry = (val: string) => {
    const nums = val.replace(/\D/g, "").slice(0, 4);
    if (nums.length >= 3) return `${nums.slice(0, 2)}/${nums.slice(2)}`;
    return nums;
  };

  const handlePay = () => {
    setProcessing(true);
    setPaymentError("");
    setTimeout(() => {
      setProcessing(false);
      setPaid(true);
      updateState({ paymentComplete: true });
    }, 2500);
  };

  const handleWalletPay = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setPaid(true);
      updateState({ paymentComplete: true });
    }, 2000);
  };

  const handleConnectBank = () => {
    setBankConnecting(true);
    setTimeout(() => {
      setBankConnecting(false);
      setBankConnected(true);
      updateState({ bankEnrolled: true });
    }, 3000);
  };

  const handleComplete = () => {
    updateState({
      policyNumber: `ART-${Date.now().toString().slice(-8)}`,
    });
    onNext();
  };

  return (
    <div className="flex flex-col px-6 py-6">
      <h2 className="text-xl font-bold mb-4">Payment</h2>

      {!paid ? (
        <>
          {/* Amount due */}
          <div className="text-center mb-4">
            <p className="text-sm text-gray-500">Due today</p>
            <p className="text-3xl font-bold">${premium}</p>
            <button
              type="button"
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="text-sm text-blue-500 flex items-center gap-1 mx-auto mt-1"
            >
              {showBreakdown ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              View breakdown
            </button>
            {showBreakdown && (
              <div className="mt-3 text-sm space-y-1 text-left bg-gray-50 rounded-xl p-4 animate-in slide-in-from-top-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Base AL Premium</span>
                  <span>${state.quote?.baseALPremium ?? 0}</span>
                </div>
                {state.quote?.ntlIncluded && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">NTL</span>
                    <span>${state.quote.ntlPrice}</span>
                  </div>
                )}
                {state.quote?.ocacAdded && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">OCAC</span>
                    <span>${state.quote.ocacPrice}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">State Fees</span>
                  <span>${state.quote?.stateFees ?? 0}</span>
                </div>
                <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                  <span>Total</span>
                  <span>${premium}</span>
                </div>
              </div>
            )}
          </div>

          {/* Apple/Google Pay */}
          <Button
            variant="outline"
            className="w-full h-12 mb-3 text-base font-semibold gap-3"
            onClick={handleWalletPay}
            disabled={processing}
          >
            <Smartphone className="w-5 h-5" />
            Pay with Apple Pay
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or pay with card</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Card entry */}
          <div className="space-y-3 mb-4">
            <div className="space-y-1">
              <Label>Card Number</Label>
              <div className="relative">
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="1234 5678 9012 3456"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  className="h-12 text-base pr-10"
                />
                <CreditCard className="absolute right-3 top-3.5 w-5 h-5 text-gray-300" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Expiry</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="MM/YY"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-1">
                <Label>CVC</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="123"
                  value={cvc}
                  onChange={(e) =>
                    setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  className="h-12 text-base"
                />
              </div>
            </div>
          </div>

          {paymentError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-600">{paymentError}</p>
            </div>
          )}

          <Button
            className="w-full h-12 text-base"
            disabled={!cardValid || processing}
            onClick={handlePay}
          >
            {processing ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" className="border-white border-t-transparent" />
                Processing...
              </span>
            ) : (
              `Pay $${premium}`
            )}
          </Button>
        </>
      ) : (
        <>
          {/* Payment success */}
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3 animate-in zoom-in">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <p className="text-lg font-semibold text-green-700">
              Payment Successful
            </p>
          </div>

          {/* Bank enrollment */}
          {!bankConnected ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Set Up Monthly Payments</h3>
              <p className="text-sm text-gray-500">
                Connect your bank account for automatic monthly payments via ACH.
                This saves you ~$11/month compared to card payments.
              </p>

              {bankError ? (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-700">
                      We couldn't connect your bank account. You can continue,
                      but please note that you'll need to set up ACH within 30
                      days to maintain your policy.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full h-12 text-base"
                    onClick={handleComplete}
                  >
                    Continue Without Bank
                  </Button>
                </>
              ) : (
                <Button
                  className="w-full h-12 text-base"
                  onClick={handleConnectBank}
                  disabled={bankConnecting}
                >
                  {bankConnecting ? (
                    <span className="flex items-center gap-2">
                      <Spinner
                        size="sm"
                        className="border-white border-t-transparent"
                      />
                      Connecting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Landmark className="w-5 h-5" />
                      Connect Bank Account
                    </span>
                  )}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-4 flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500 shrink-0" />
                  <div>
                    <p className="font-medium">Chase Bank ****4521</p>
                    <p className="text-sm text-gray-500">
                      First ACH payment: April 18, 2026
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Button
                className="w-full h-12 text-base"
                onClick={handleComplete}
              >
                Complete
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
